import React, { useState, useEffect } from 'react';
import { Table, message, Row, Col, Space, Button, Input, Select, Popconfirm } from 'antd';
import { 
  DesktopOutlined, 
  ClusterOutlined, 
  EditOutlined, 
  PlusOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  DeploymentUnitOutlined,
  EyeOutlined,
  SaveOutlined,
  CloseOutlined,
  SearchOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;

const MobileMachineMappings = () => {
  const [boxes, setBoxes] = useState([]);
  const [machines, setMachines] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMappings, setActiveMappings] = useState({});
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [editingBoxId, setEditingBoxId] = useState(null);
  const [searchText, setSearchText] = useState(''); 
  
  const [selectedBox, setSelectedBox] = useState(null);
  const [rows, setRows] = useState([{ machine: undefined, left_field: '', right_field: '' }]);

  const BOX_URL = '/api/box/list/';
  const MACHINES_URL = '/api/box/machines-api/';
  const MAPPINGS_URL = '/api/box/mappings-api/';
  const SEND_MAPPING_URL = '/api/box/send-mapping/';
  const HIDE_MAPPING_URL = '/api/box/hide-mapping/';

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resBoxes, resMachines, resMappings] = await Promise.all([
        axios.get(BOX_URL),
        axios.get(MACHINES_URL),
        axios.get(MAPPINGS_URL)
      ]);
      setBoxes(resBoxes.data.filter(b => b.id !== 294));
      setMachines(resMachines.data);
      setMappings(resMappings.data);
    } catch (error) {
      message.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredBoxes = boxes.filter(box => {
    const search = searchText.toLowerCase();
    return (
      (box.box_name || '').toLowerCase().includes(search) ||
      (box.box_id || '').toString().toLowerCase().includes(search)
    );
  });

  const toggleMapping = async (record) => {
    const boxId = record.box_id;
    const isCurrentlyActive = activeMappings[boxId];
    try {
      if (!isCurrentlyActive) {
        await axios.post(SEND_MAPPING_URL, { box_id: boxId });
        setActiveMappings(prev => ({ ...prev, [boxId]: true }));
        message.success(`Mapping shown on ${record.box_name}`);
      } else {
        await axios.post(HIDE_MAPPING_URL, { box_id: boxId });
        setActiveMappings(prev => ({ ...prev, [boxId]: false }));
        message.info(`Mapping hidden on ${record.box_name}`);
      }
    } catch (error) {
      message.error('Command failed');
    }
  };

  const openOverlay = (boxRecord = null) => {
    if (boxRecord) {
      setEditingBoxId(boxRecord.id);
      setSelectedBox(boxRecord.id);
      const boxMappings = mappings
        .filter(m => m.box === boxRecord.id)
        .sort((a, b) => (parseInt(a.machine_display) || 0) - (parseInt(b.machine_display) || 0));
      
      setRows(boxMappings.length > 0 ? boxMappings.map(m => ({
        machine: m.machine_display,
        left_field: m.left_field,
        right_field: m.right_field
      })) : [{ machine: undefined, left_field: '', right_field: '' }]);
    } else {
      setEditingBoxId(null);
      setSelectedBox(null);
      setRows([{ machine: undefined, left_field: '', right_field: '' }]);
    }
    setIsOverlayOpen(true);
  };

  const handleSave = async () => {
    if (!selectedBox) { message.warning('Select a box'); return; }
    const validRows = rows.filter(r => r.machine && r.left_field !== "" && r.right_field !== "");
    
    try {
      if (editingBoxId) {
        const oldMappings = mappings.filter(m => m.box === editingBoxId);
        await Promise.all(oldMappings.map(m => axios.delete(`${MAPPINGS_URL}${m.id}/`)));
      }
      
      await Promise.all(validRows.map(row => 
        axios.post(MAPPINGS_URL, {
          box: selectedBox,
          machine: row.machine,
          left_field: row.left_field,
          right_field: row.right_field
        })
      ));
      
      message.success('Mapping saved');
      setIsOverlayOpen(false);
      fetchData();
    } catch (error) {
      message.error('Error saving');
    }
  };

  const deleteMappings = async (boxId) => {
    const boxMappings = mappings.filter(m => m.box === boxId);
    try {
      await Promise.all(boxMappings.map(m => axios.delete(`${MAPPINGS_URL}${m.id}/`)));
      message.success('Mappings removed');
      fetchData();
    } catch (error) {
      message.error('Error deleting');
    }
  };

  const columns = [
    {
      title: 'Mappings',
      key: 'mapping_card',
      render: (record) => {
        const boxMappings = mappings
          .filter(m => m.box === record.id)
          .sort((a, b) => Number(a.machine_display) - Number(b.machine_display));
        const isActive = activeMappings[record.box_id];

        return (
          <div className="m-map-card">
            <div className="m-map-header">
              <Space>
                <DesktopOutlined style={{ color: record.status === 'online' ? '#34d399' : '#64748b' }} />
                <span className="m-map-boxname">{record.box_name || 'No Name'}</span>
                <span className="m-map-boxid">({record.box_id})</span>
              </Space>
            </div>

            <div className="m-map-list">
              {boxMappings.length === 0 ? (
                <div style={{ color: '#475569', fontSize: '12px', fontStyle: 'italic' }}>No machines linked</div>
              ) : (
                boxMappings.map(m => (
                  <div key={m.id} className="m-map-item">
                    <ClusterOutlined style={{ color: '#64748b' }} />
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>{m.machine_display}</span>
                    <span style={{ color: '#94a3b8' }}>[{m.left_field}, {m.right_field}]</span>
                  </div>
                ))
              )}
            </div>

            <div className="m-map-actions">
              <Button 
                size="small" 
                icon={<EyeOutlined />} 
                onClick={() => toggleMapping(record)}
                style={{ 
                  background: isActive ? '#059669' : '#1e293b', 
                  color: isActive ? 'white' : '#94a3b8', 
                  border: isActive ? 'none' : '1px solid #334155' 
                }}
              >
                {isActive ? 'Hide' : 'Show'}
              </Button>
              <Button size="small" icon={<EditOutlined />} onClick={() => openOverlay(record)} style={{ background: '#1e293b', color: '#60a5fa', border: '1px solid #334155' }}>Edit</Button>
              <Popconfirm title="Delete all?" onConfirm={() => deleteMappings(record.id)}>
                <Button size="small" icon={<DeleteOutlined />} style={{ background: '#1e293b', color: '#ef4444', border: '1px solid #334155' }}>Del</Button>
              </Popconfirm>
            </div>
          </div>
        );
      }
    }
  ];

  return (
    <div id="mobile-mapping-container">
      <Row gutter={[8, 8]} style={{ marginBottom: '15px' }}>
        <Col span={12}>
          <div className="st-box" style={{ borderBottomColor: '#3b82f6' }}>
            <Space>
              <DeploymentUnitOutlined style={{ color: '#3b82f6' }} />
              <span style={{ color: '#64748b', fontSize: '11px' }}>MAPPED:</span>
              <span style={{ color: 'white', fontWeight: 'bold' }}>{[...new Set(mappings.map(m => m.box))].length}</span>
            </Space>
          </div>
        </Col>
        <Col span={12}>
          <div className="st-box" style={{ borderBottomColor: '#3b82f6', padding: '0' }}>
            <Button block type="dashed" icon={<PlusOutlined />} onClick={() => openOverlay()} style={{ height: '43px', background: 'transparent', color: '#3b82f6', border: 'none', fontWeight: 'bold' }}>
              NEW LINK
            </Button>
          </div>
        </Col>
      </Row>

      <div style={{ marginBottom: '15px' }}>
        <Input
          placeholder="Pretraži boxove..."
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          onChange={e => setSearchText(e.target.value)}
          className="m-search-input"
          allowClear
        />
      </div>

      <Table dataSource={filteredBoxes} columns={columns} rowKey="id" loading={loading} pagination={false} className="m-map-table" />

      {isOverlayOpen && (
        <div className="m-custom-overlay">
          <div className="m-overlay-content">
            <div className="m-overlay-header">
              <span>{editingBoxId ? 'EDIT LINK' : 'CREATE LINK'}</span>
              <Button type="text" icon={<CloseOutlined style={{ color: 'white' }} />} onClick={() => setIsOverlayOpen(false)} />
            </div>
            <div className="m-overlay-body" id="overlay-scroll-area">
              <label className="m-lab">TARGET BOX</label>
              <Select 
                showSearch // AKTIVIRAN FILTER
                optionFilterProp="children" // FILTRIRA PO TEKSTU OPCIJE
                getPopupContainer={() => document.getElementById('overlay-scroll-area')}
                style={{ width: '100%', marginBottom: '20px' }} 
                value={selectedBox} 
                onChange={v => setSelectedBox(v)}
                placeholder="Select box"
                className="m-dark-select"
              >
                {boxes.map(b => <Option key={b.id} value={b.id}>{b.box_name || b.box_id}</Option>)}
              </Select>

              <label className="m-lab">MACHINE MAPPINGS</label>
              <div className="m-rows-container">
                {rows.map((row, index) => (
                  <div key={index} className="m-row-item">
                    <Select 
                      showSearch // AKTIVIRAN FILTER
                      optionFilterProp="children" // FILTRIRA PO TEKSTU OPCIJE
                      getPopupContainer={() => document.getElementById('overlay-scroll-area')}
                      style={{ width: '40%' }} 
                      value={row.machine} 
                      onChange={v => {
                        const newRows = [...rows];
                        newRows[index].machine = v;
                        setRows(newRows);
                      }}
                      placeholder="Machine"
                      className="m-dark-select"
                    >
                      {machines.map(m => <Option key={m.id} value={m.machine_id}>{m.machine_id}</Option>)}
                    </Select>
                    <Input style={{ width: '25%' }} placeholder="L" value={row.left_field} onChange={e => {
                      const newRows = [...rows];
                      newRows[index].left_field = e.target.value;
                      setRows(newRows);
                    }} className="m-dark-input" />
                    <Input style={{ width: '25%' }} placeholder="R" value={row.right_field} onChange={e => {
                      const newRows = [...rows];
                      newRows[index].right_field = e.target.value;
                      setRows(newRows);
                    }} className="m-dark-input" />
                    <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => setRows(rows.filter((_, i) => i !== index))} />
                  </div>
                ))}
                <Button type="dashed" block onClick={() => setRows([...rows, { machine: undefined, left_field: '', right_field: '' }])} style={{ marginTop: '10px', color: '#94a3b8' }}>
                  + Add Machine
                </Button>
              </div>

              <Button block type="primary" icon={<SaveOutlined />} onClick={handleSave} style={{ background: '#3b82f6', height: '45px', marginTop: '20px', fontWeight: 'bold' }}>
                SAVE MAPPINGS
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .st-box { background: #1e293b; padding: 12px; border-radius: 8px; border: 1px solid #334155; border-bottom: 3px solid; display: flex; justify-content: center; align-items: center; }
        
        .m-search-input {
          background: #1e293b !important;
          border: 1px solid #334155 !important;
          color: white !important;
          border-radius: 8px !important;
          height: 45px !important;
        }
        .m-search-input input {
          background: transparent !important;
          color: white !important;
        }
        .m-search-input .ant-input-clear-icon { color: #94a3b8 !important; }

        .m-map-table .ant-table { background: transparent !important; }
        .m-map-table .ant-table-thead { display: none !important; }
        .m-map-table .ant-table-tbody > tr > td { border: none !important; padding: 0 0 12px 0 !important; }
        .m-map-table .ant-table-tbody > tr:hover > td { background: transparent !important; }

        .m-map-card { background: #1e293b; border-radius: 8px; border: 1px solid #334155; padding: 12px; }
        .m-map-header { margin-bottom: 12px; }
        .m-map-boxname { color: white; font-weight: bold; }
        .m-map-boxid { color: #64748b; font-size: 11px; margin-left: 5px; }
        
        .m-map-list { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 15px; }
        .m-map-item { background: #0f172a; padding: 4px 8px; border-radius: 4px; border: 1px solid #334155; font-size: 11px; display: flex; align-items: center; gap: 6px; }

        .m-map-actions { display: flex; gap: 8px; justify-content: flex-end; }

        .m-custom-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.85); z-index: 9999; display: flex; align-items: center; justify-content: center; }
        .m-overlay-content { background: #1e293b; width: 90%; border-radius: 12px; border: 1px solid #334155; overflow: visible; position: relative; }
        .m-overlay-header { padding: 15px; border-bottom: 1px solid #334155; display: flex; justify-content: space-between; align-items: center; color: white; font-weight: bold; }
        .m-overlay-body { padding: 15px; max-height: 80vh; overflow-y: auto; position: relative; }
        
        .m-lab { color: #64748b; font-size: 10px; font-weight: bold; display: block; margin-bottom: 5px; }
        .m-dark-input { background: #0f172a !important; color: white !important; border: 1px solid #334155 !important; }
        .m-row-item { display: flex; gap: 5px; margin-bottom: 8px; align-items: center; }
        
        .ant-select-selector { background: #0f172a !important; color: white !important; border: 1px solid #334155 !important; }
        
        .ant-select-dropdown { 
          z-index: 10000 !important; 
        }
      `}</style>
    </div>
  );
};

export default MobileMachineMappings;